import { Breadcrumbs, Stack, Typography } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-premium';
import React, { useMemo, useState } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';

import ContractCtrData from '../components/Contracts/ContractCtrData';
import ContractEditPageLink from '../components/Contracts/ContractEditPageLink';
import ContractLatestReportButton from '../components/Contracts/ContractLatestReportButton';
import ContractManager from '../components/Contracts/ContractManager';
import ContractPacingData from '../components/Contracts/ContractPacingData';
import ContractSummaryData from '../components/Contracts/ContractSummaryData';
import { formatFlightDate } from '../components/Contracts/KPIHeader';
import useContract from '../hooks/useContract';
import { ContractManagerContext } from '../hooks/useContractManagerContext';
import mapContractToForm from '../util/mapContractToForm';
import { Contract } from '../validationSchemas/ContractSchema';

const TAB_LIST = ['Line Items', 'Ad Groups', 'Creatives'];

function ContractReportPage() {
  const params = useParams();
  const contractId = Number(params.id);

  const { data, isLoading } = useContract(contractId);

  const contract: Contract = mapContractToForm(data || {});

  const { contractName, startDate, endDate } = contract;

  // Pre-filter on a specific line item when arrived from the Daily Spend modal (ADOPS-2642 stretch).
  const [searchParams] = useSearchParams();
  const initialLineItemId = searchParams.get('lineItemId');
  const initialSelectedLineItemIds: GridRowSelectionModel = initialLineItemId ? [Number(initialLineItemId)] : [];

  const [activeTab, setActiveTab] = useState(TAB_LIST[0]);
  const [tabIcons, setTabIcons] = useState<{ [tab: string]: any }>({});
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<GridRowSelectionModel>(initialSelectedLineItemIds);
  const [selectedLineItems, setSelectedLineItems] = useState<any[]>([]);
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState<GridRowSelectionModel>([]);
  const [selectedAdGroups, setSelectedAdGroups] = useState<any[]>([]);

  const value = useMemo(
    () => ({
      contract,
      selectedLineItems,
      setSelectedLineItems,
      selectedLineItemIds,
      setSelectedLineItemIds,
      selectedAdGroups,
      setSelectedAdGroups,
      selectedAdGroupIds,
      setSelectedAdGroupIds,
      tabIcons,
      setTabIcons,
      isLoading,
      activeTab,
      setActiveTab,
    }),
    [
      contract,
      isLoading,
      selectedLineItems,
      setSelectedLineItems,
      selectedLineItemIds,
      setSelectedLineItemIds,
      selectedAdGroupIds,
      setSelectedAdGroupIds,
      selectedAdGroups,
      setSelectedAdGroups,
      tabIcons,
      setTabIcons,
      activeTab,
      setActiveTab,
    ]
  );

  return (
    <Stack>
      <Breadcrumbs sx={{ p: 2, fontSize: '12px' }}>
        <NavLink to="/" color="#616161">
          Home
        </NavLink>
        <span color="#2D2D2D">{contractName}</span>
      </Breadcrumbs>
      <ContractManagerContext.Provider value={value}>
        <Stack spacing={2} px={2} pb={2}>
          <Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h4">Contract Performance - {contractName}</Typography>
              <Stack mt={1} direction="row" spacing={1}>
                <ContractLatestReportButton contractId={contractId} />
                <ContractEditPageLink contractId={contractId} />
              </Stack>
            </Stack>
            <Typography variant="h6">Flight Dates: {formatFlightDate(startDate, endDate)}</Typography>
          </Stack>
          <ContractSummaryData contractId={contractId} />
          <Stack spacing={2} direction="row">
            <ContractPacingData contractId={contractId} />
            <ContractCtrData contractId={contractId} />
          </Stack>
          <ContractManager tabList={TAB_LIST} />
        </Stack>
      </ContractManagerContext.Provider>
    </Stack>
  );
}

export default ContractReportPage;
